import React, { useState, useEffect } from 'react';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import './MainScreen.css';
import HorizontalCarousel from '../components/HorizontalCarousel.tsx';
import Loader from '../components/Loader';
import { useApi } from '../services/ApiServices.tsx';
import DynamicCarousel from '../components/DynamicCarousel.tsx';
import { Grid2, Typography } from '@mui/material';
import { TipoReceta } from '../interfaces/TipoReceta.interface.ts';
import Header from '../components/Header.tsx';
import Footer from '../components/Footer.tsx';
import TipsComponent from '../components/Tips/TipsComponent.tsx';
import { Banner } from '../interfaces/Banner.interface.ts';
import BannerCarousel from '../components/BannerCarousel/BannerCarousel.tsx';
import { useNavigate } from 'react-router-dom';
import BrandCarousel from '../components/BrandCarousel/BrandCarousel.tsx';
import { useAuth } from '../components/Auth/AuthContext.tsx';
import { Receta } from '../interfaces/RecetaDetalle.interface.ts';

function MainScreen() {
  const { ApiRequest } = useApi();
  const { user } = useAuth();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [sectionsToCook, setSectionsToCook] = useState<TipoReceta[]>([]);
  const [customYourExperience, setCustomYourExperience] = useState<TipoReceta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingBanner, setIsLoadingBanner] = useState(true);
  const [step, setStep] = useState(0);
  const [favoritas, setFavoritas] = useState<Receta[]>([]);
  const [isLoadingFavoritas, setIsLoadingFavoritas] = useState(true);

  const [tiempo, setTiempo] = useState('');
  const [momento, setMomento] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSectionsToCook = async () => {
      try {
        setIsLoading(true);
        const data = await ApiRequest('api/TiemposDia/ListarTiemposDia');
        setSectionsToCook(data || []);
        setCustomYourExperience(data || []);
      } catch (error) {
        console.error('Error fetching recetas:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchBanners = async () => {
      try {
        setIsLoadingBanner(true);
        const data = await ApiRequest('api/Banner');
        setBanners(data || []);
      } catch (error) {
        console.error('Error fetching baners:', error);
      } finally {
        setIsLoadingBanner(false);
      }
    };

    fetchSectionsToCook();
    fetchBanners();
  }, [ApiRequest]);

  async function getRecetasPorTipo(section: TipoReceta): Promise<any> {
    try {
      const body = JSON.stringify({ tipo: section.nombre });
      const data = await ApiRequest('api/Receta/MasRecientesPorTipo', 'POST', body);

      if (data && Array.isArray(data) && data.length > 0) {
        return data; 
      } else {
        console.warn('No se recibieron datos válidos');
        return [];
      }
    } catch (error) {
      console.error('Error fetching recetas:', error);
      return [];
    }
  }

  useEffect(() => {
    const fetchRecetasFavoritas = async () => {
      setIsLoadingFavoritas(true);
      if (user) {
        const data = await getRecetasFavoritas();
        setFavoritas(data);
      }
      setIsLoadingFavoritas(false);
    };

    fetchRecetasFavoritas();
  }, [user]);

  async function getRecetasFavoritas(): Promise<any[]> {
    try {
      const url = `api/Receta/ListasRecetasFavoritas?email=${user?.email ?? ""}`;
      const data = await ApiRequest(url, 'GET');

      if (data && Array.isArray(data) && data.length > 0) {
        return data;
      } else {
        console.warn('No se recibieron datos válidos en favoritas');
        return [];
      }
    } catch (error) {
      console.error('Error fetching recetas favoritas:', error);
      return [];
    }
  }

  async function getMomentos(): Promise<any> {
    try {
      const data = await ApiRequest('api/Catalogo/ListarMomentosDia');

      if (data && Array.isArray(data) && data.length > 0) {
        return data; 
      } else {
        console.warn('No se recibieron datos válidos');
        return [];
      }
    } catch (error) {
      console.error('Error fetching recetas:', error);
      return [];
    }
  }

  async function getCategorias(): Promise<any> {
    try {
      const data = await ApiRequest('api/Catalogo/ListarCategoriasAlimenticias');

      if (data && Array.isArray(data) && data.length > 0) {
        return data; 
      } else {
        console.warn('No se recibieron datos válidos');
        return [];
      }
    } catch (error) {
      console.error('Error fetching recetas:', error);
      return [];
    }
  }

  const handleSectionClick = async (id: string) => {
    if (step === 0) {
      setStep(1);
      setTiempo(id);
      setIsLoading(true);
      const momentos = await getMomentos();
      setCustomYourExperience(momentos || []);
      setIsLoading(false);
    } else if (step === 1) {
      setStep(2);
      setMomento(id);
      setIsLoading(true);
      const categorias = await getCategorias();
      setCustomYourExperience(categorias || []);
      setIsLoading(false);
    } else if (step === 2) {
      setStep(0);

      // Construcción de la URL con los parámetros
      const urlParams = new URLSearchParams();
      urlParams.append('tiempo', tiempo); // Asegúrate de que `tiempo` tenga un valor
      urlParams.append('momento', momento); // Asegúrate de que `momento` tenga un valor
      urlParams.append('categoria', id); // `id` es la categoría seleccionada

      // Redirigir a la URL construida
      navigate(`/recetas?${urlParams.toString()}`);
    }
  };

  return (
    <Grid2 className="MainScreen">
      <Header />

      {!isLoadingBanner && banners.length > 0 && (
        <BannerCarousel
          banners={banners}
        />
      )}

      
      <Grid2 className="text-section">
        <p className="primary-text">Personaliza tu experiencia</p>
        <p className="secondary-text">¿Qué quieres cocinar hoy?</p>
      </Grid2>

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <HorizontalCarousel 
            items={customYourExperience} 
            onItemClick={handleSectionClick} // Pasar el manejador de clic
          />
          <Grid2 className='marging-medium'></Grid2>
        </>
      )}

      {user && (
        <>
        {isLoadingFavoritas ? (
          <Loader />
        ) : (
        <Grid2 container className='recetas-container'>
          <Grid2 size={12}>
            <Grid2 className='recetas-title'><span className="material-icons favorite-icon">{"favorite"}</span>Recetas Favoritas</Grid2>
            <Grid2 className='recetas-description'>Aquí podrás encontrar las recetas que has guardado como favoritas</Grid2>
          </Grid2>
          {favoritas.length > 0 ? (
            <Grid2 size={12} key={'favoritas'}>
              <DynamicCarousel
                section={null}
                onLoadItems={() => Promise.resolve(favoritas)}
                favorito={true}
              />
            </Grid2>
          ) : (
            <p>No se encontraron recetas favoritas guardadas</p>
          )}
        </Grid2>
        )}
        </>
      )}

      <Grid2 container className='recetas-container'>
        <Grid2 size={12}>
          <Grid2 className='recetas-title'>Recetas</Grid2>
          <Grid2 className='recetas-description'>Descubre los mejores consejos de cocina, limpieza, alimentación y más.</Grid2>
        </Grid2>
        {sectionsToCook && sectionsToCook.length > 0 ? (
          sectionsToCook.map((section) => (
            <Grid2 size={12} key={section.nombre}>
              <DynamicCarousel
                section={section}
                onLoadItems={() => getRecetasPorTipo(section)}
              />
            </Grid2>
          ))
        ) : (
          <p>No se encontraron recetas recientes</p>
        )}
      </Grid2>

      <Grid2 size={12} className='tips-main-container'>
        <Typography className='tip-main-title'>Tips</Typography>
        <Typography className='tip-main-subtitle'>Descubre los mejores consejos de cocina, limpieza, alimentación y más</Typography>
      </Grid2>
      <TipsComponent />


      <Grid2>
        <BrandCarousel showTitle={true} />
      </Grid2>

      <Footer />
    </Grid2>
  );
}

export default MainScreen;
